import { useEffect, useState, useCallback } from 'react';
import {
  Settings,
  Key,
  BarChart3,
  RefreshCw,
  Eye,
  EyeOff,
  Plus,
  Pencil,
  Trash2,
  ToggleLeft,
  ToggleRight,
  X,
  ChevronDown,
  ChevronRight,
  FileText,
  Globe,
  Database,
  Upload,
  Image,
  Users,
  Check,
  Wand2,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { PromptInstructionsPanel } from './PromptInstructionsPanel';
import { siteProxy } from '../../services/siteProxy';
import { authService } from '../../services/auth';

// ─── Types ───────────────────────────────────────────────────────────

interface SystemSettings {
  showCreativity: boolean;
  showRepeats: boolean;
  showImageSearch: boolean;
  showGoogleSearch: boolean;
  language: string;
  theme: string;
  newYearMode: boolean;
  mediaResolution: string;
  apiProvider: string;
  externalGalleryHiddenUsers: string[];
  [key: string]: unknown;
}

interface ServerApiKey {
  id: string;
  provider: string;
  label: string;
  enabled: boolean;
  allowedUsers: string[];
  createdAt: string;
}

interface UserStats {
  totalTokens: number;
  totalCost: number;
  count: number;
}

interface Preset {
  name: string;
  content: string;
}

interface S3Status {
  s3Enabled: boolean;
  endpoint: string | null;
  bucket: string | null;
  cdnUrl: string | null;
  keyPrefix: string;
  galleryBucket: string | null;
  galleryEnabled: boolean;
}

interface AiMigrationStatus {
  running: boolean;
  pending: { images: number; total: number };
  existing: { images: number; total: number };
}

interface AiMigrationProgress {
  phase: string;
  uploaded: number;
  total: number;
  skipped: number;
  errors: string[];
}

interface GalleryMigrationStatus {
  running: boolean;
  pending: number;
  existing: number;
}

// ─── Collapsible Section ─────────────────────────────────────────────

function Section({
  title,
  icon: Icon,
  children,
  defaultOpen = false,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="rounded-xl border border-white/[0.06] bg-slate-800/40 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-white/[0.02] transition"
      >
        <Icon className="w-4.5 h-4.5 text-slate-400" />
        <span className="text-base font-medium text-slate-200 flex-1">{title}</span>
        {open ? (
          <ChevronDown className="w-4 h-4 text-slate-500" />
        ) : (
          <ChevronRight className="w-4 h-4 text-slate-500" />
        )}
      </button>
      {open && <div className="px-5 pb-5 border-t border-white/[0.04]">{children}</div>}
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────

export function AiLabPage() {
  return (
    <div className="max-w-6xl mx-auto space-y-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-100">AI Lab</h1>
        <p className="text-sm text-slate-500 mt-1">Настройки ai.witelab.ru</p>
      </div>

      <Section title="Системные настройки" icon={Settings} defaultOpen>
        <SystemSettingsPanel />
      </Section>

      <Section title="API ключи" icon={Key}>
        <ApiKeysPanel />
      </Section>

      <Section title="Глобальные пресеты" icon={FileText}>
        <PresetsPanel />
      </Section>

      <Section title="Инструкции Prompt Assistant" icon={Wand2}>
        <PromptInstructionsPanel />
      </Section>

      <Section title="Ключ галереи" icon={Globe}>
        <GalleryKeyPanel />
      </Section>

      <Section title="Видимость пользователей в API галереи" icon={Users}>
        <GalleryVisibilityPanel />
      </Section>

      <Section title="Статистика использования" icon={BarChart3}>
        <StatsPanel />
      </Section>

      <Section title="S3 хранилище" icon={Database}>
        <S3Panel />
      </Section>

      <Section title="Миграция галереи" icon={Image}>
        <GalleryMigrationPanel />
      </Section>
    </div>
  );
}

// ─── System Settings ─────────────────────────────────────────────────

function SystemSettingsPanel() {
  const [settings, setSettings] = useState<SystemSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setError('');
      const data = (await siteProxy('ai', '/api/system-settings')) as SystemSettings;
      setSettings(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить настройки');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (key: keyof SystemSettings) => {
    if (!settings) return;
    setSettings({ ...settings, [key]: !settings[key] });
  };

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    await siteProxy('ai', '/api/system-settings', { method: 'POST', body: settings });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  if (error) {
    return <div className="py-4 text-sm text-red-400">Ошибка: {error}</div>;
  }
  if (!settings) {
    return <div className="py-4 text-sm text-slate-500">Загрузка...</div>;
  }

  const toggleItems: { key: keyof SystemSettings; label: string }[] = [
    { key: 'showCreativity', label: 'Креативность' },
    { key: 'showRepeats', label: 'Повторы' },
    { key: 'showImageSearch', label: 'Поиск изображений' },
    { key: 'showGoogleSearch', label: 'Google поиск' },
    { key: 'newYearMode', label: 'Новогодний режим' },
  ];

  return (
    <div className="pt-4 space-y-4">
      {/* Toggles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {toggleItems.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => toggle(key)}
            className="flex items-center justify-between p-3 rounded-lg bg-slate-900/60 border border-white/[0.06] hover:border-white/[0.1] transition"
          >
            <span className="text-sm text-slate-300">{label}</span>
            {settings[key] ? (
              <ToggleRight className="w-5 h-5 text-emerald-400" />
            ) : (
              <ToggleLeft className="w-5 h-5 text-slate-600" />
            )}
          </button>
        ))}
      </div>

      {/* Selects */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs text-slate-500 mb-1">Язык</label>
          <select
            value={settings.language}
            onChange={(e) => setSettings({ ...settings, language: e.target.value })}
            className="input"
          >
            <option value="ru">Русский</option>
            <option value="en">English</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Тема</label>
          <select
            value={settings.theme}
            onChange={(e) => setSettings({ ...settings, theme: e.target.value })}
            className="input"
          >
            <option value="default">По умолчанию</option>
            <option value="raspberry">Raspberry</option>
            <option value="green">Green</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Разрешение</label>
          <select
            value={settings.mediaResolution}
            onChange={(e) => setSettings({ ...settings, mediaResolution: e.target.value })}
            className="input"
          >
            <option value="LOW">Низкое</option>
            <option value="MEDIUM">Среднее</option>
            <option value="HIGH">Высокое</option>
          </select>
        </div>
      </div>

      {/* Yandex Folder ID */}
      <div>
        <label className="block text-xs text-slate-500 mb-1">Yandex Folder ID (для YandexART)</label>
        <input
          type="text"
          value={(settings as any).yandexFolderId || ''}
          onChange={(e) => setSettings({ ...settings, yandexFolderId: e.target.value })}
          className="input"
          placeholder="b1g..."
        />
      </div>

      {/* Save */}
      <div className="flex gap-3 pt-2">
        <button
          onClick={save}
          disabled={saving}
          className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm transition disabled:opacity-50"
        >
          {saving ? 'Сохранение...' : saved ? '✓ Сохранено' : 'Сохранить'}
        </button>
        <button
          onClick={load}
          className="px-4 py-2 rounded-lg bg-slate-800 text-slate-400 hover:bg-slate-700 text-sm transition"
        >
          Сбросить
        </button>
      </div>
    </div>
  );
}

// ─── API Keys ────────────────────────────────────────────────────────

function ApiKeysPanel() {
  const [keys, setKeys] = useState<ServerApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [revealed, setRevealed] = useState<Record<string, string>>({});

  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = (await siteProxy('ai', '/api/server-keys?management=true')) as ServerApiKey[];
      setKeys(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить ключи');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleKey = async (id: string) => {
    await siteProxy('ai', `/api/server-keys/${id}/toggle`, { method: 'POST' });
    load();
  };

  const deleteKey = async (id: string) => {
    if (!confirm('Удалить ключ?')) return;
    await siteProxy('ai', `/api/server-keys/${id}`, { method: 'DELETE' });
    load();
  };

  const revealKey = async (id: string) => {
    if (revealed[id]) {
      setRevealed((r) => {
        const copy = { ...r };
        delete copy[id];
        return copy;
      });
      return;
    }

    const data = (await siteProxy('ai', `/api/server-keys/${id}/reveal?management=true`)) as {
      key?: string;
    };
    if (data?.key) {
      setRevealed((r) => ({ ...r, [id]: data.key! }));
    }
  };

  if (loading) return <div className="py-4 text-sm text-slate-500">Загрузка...</div>;
  if (error) return <div className="py-4 text-sm text-red-400">Ошибка: {error}</div>;

  return (
    <div className="pt-4 space-y-3">
      <div className="flex justify-end">
        <button
          onClick={() => setCreating(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs bg-indigo-600 hover:bg-indigo-500 text-white transition"
        >
          <Plus className="w-3.5 h-3.5" />
          Добавить
        </button>
      </div>

      {keys.length === 0 ? (
        <div className="py-6 text-center text-slate-600 text-sm">Нет API ключей</div>
      ) : (
        keys.map((key) => (
          <div
            key={key.id}
            className="p-4 rounded-lg bg-slate-900/60 border border-white/[0.06]"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-200">{key.label}</span>
                <span
                  className={`px-1.5 py-0.5 text-[10px] rounded-full font-medium ${
                    key.provider === 'google'
                      ? 'bg-blue-500/10 text-blue-400'
                      : key.provider === 'yandex'
                      ? 'bg-yellow-500/10 text-yellow-400'
                      : key.provider === 'grok'
                      ? 'bg-emerald-500/10 text-emerald-400'
                      : key.provider === 'openai'
                      ? 'bg-cyan-500/10 text-cyan-400'
                      : 'bg-purple-500/10 text-purple-400'
                  }`}
                >
                  {key.provider === 'google' ? 'Google'
                    : key.provider === 'yandex' ? 'Yandex'
                    : key.provider === 'grok' ? 'Grok'
                    : key.provider === 'openai' ? 'OpenAI'
                    : key.provider === 'suno' ? 'Suno'
                    : key.provider}
                </span>
                <span
                  className={`px-1.5 py-0.5 text-[10px] rounded-full font-medium ${
                    key.enabled
                      ? 'bg-emerald-500/10 text-emerald-400'
                      : 'bg-slate-700 text-slate-500'
                  }`}
                >
                  {key.enabled ? 'активен' : 'отключен'}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => revealKey(key.id)}
                  className="p-1.5 rounded text-slate-500 hover:text-amber-400 transition"
                  title="Показать ключ"
                >
                  {revealed[key.id] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
                <button
                  onClick={() => toggleKey(key.id)}
                  className="p-1.5 rounded text-slate-500 hover:text-indigo-400 transition"
                  title={key.enabled ? 'Отключить' : 'Включить'}
                >
                  {key.enabled ? <ToggleRight className="w-3.5 h-3.5" /> : <ToggleLeft className="w-3.5 h-3.5" />}
                </button>
                <button
                  onClick={() => setEditing(key.id)}
                  className="p-1.5 rounded text-slate-500 hover:text-indigo-400 transition"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => deleteKey(key.id)}
                  className="p-1.5 rounded text-slate-500 hover:text-red-400 transition"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            {revealed[key.id] && (
              <div className="mt-2 p-2 rounded bg-slate-950 border border-white/[0.04] text-xs font-mono text-amber-300 break-all">
                {revealed[key.id]}
              </div>
            )}
            <div className="text-xs text-slate-600 mt-1 flex items-center gap-1.5 flex-wrap">
              <span>Доступ:</span>
              {key.allowedUsers.includes('all') ? (
                <span className="px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 text-[10px] font-medium">
                  все
                </span>
              ) : (
                key.allowedUsers.map((uid) => (
                  <span
                    key={uid}
                    className="px-1.5 py-0.5 rounded-full bg-indigo-500/10 text-indigo-400 text-[10px] font-medium"
                  >
                    {uid}
                  </span>
                ))
              )}
            </div>
          </div>
        ))
      )}

      {(creating || editing) && (
        <ApiKeyModal
          keyId={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={load}
        />
      )}
    </div>
  );
}

// ─── API Key Modal ───────────────────────────────────────────────────

function ApiKeyModal({
  keyId,
  onClose,
  onSaved,
}: {
  keyId: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!keyId;
  const [provider, setProvider] = useState('google');
  const [label, setLabel] = useState('');
  const [keyValue, setKeyValue] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [allUsersMode, setAllUsersMode] = useState(true);
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [loadingKey, setLoadingKey] = useState(false);

  // User list for picker
  const [userList, setUserList] = useState<{ id: string; username: string }[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [userSearch, setUserSearch] = useState('');

  // Load user list
  useEffect(() => {
    (async () => {
      try {
        const res = await authService.apiFetch('/api/auth/users?limit=500');
        const data = await res.json();
        if (data.success) {
          setUserList(data.data.users.map((u: { id: string; username: string }) => ({
            id: u.id,
            username: u.username,
          })));
        }
      } catch { /* ignore */ }
      setLoadingUsers(false);
    })();
  }, []);

  // Load existing key data when editing
  useEffect(() => {
    if (!isEdit) return;
    setLoadingKey(true);
    (async () => {
      try {
        const keys = await siteProxy('ai', '/api/server-keys?management=true') as Array<{
          id: string; provider: string; label: string; allowedUsers: string[]; enabled: boolean;
        }>;
        const key = Array.isArray(keys) ? keys.find((k) => k.id === keyId) : null;
        if (key) {
          setProvider(key.provider);
          setLabel(key.label);
          setEnabled(key.enabled);
          if (key.allowedUsers.includes('all')) {
            setAllUsersMode(true);
            setSelectedUsers([]);
          } else {
            setAllUsersMode(false);
            setSelectedUsers(key.allowedUsers);
          }
        }
      } catch { /* ignore */ }
      setLoadingKey(false);
    })();
  }, [isEdit, keyId]);

  const toggleUser = (userId: string) => {
    setSelectedUsers((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    );
  };

  const filteredUsers = userSearch
    ? userList.filter(
        (u) =>
          u.username.toLowerCase().includes(userSearch.toLowerCase()) ||
          u.id.toLowerCase().includes(userSearch.toLowerCase()),
      )
    : userList;

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    const body: Record<string, unknown> = {
      provider,
      label,
      enabled,
      allowedUsers: allUsersMode ? ['all'] : selectedUsers,
    };

    if (isEdit) {
      body.id = keyId;
    }
    if (keyValue) {
      body.key = keyValue;
    }

    if (!allUsersMode && selectedUsers.length === 0) {
      setError('Выберите хотя бы одного пользователя');
      setSaving(false);
      return;
    }

    const res = (await siteProxy('ai', '/api/server-keys', {
      method: 'POST',
      body,
    })) as { error?: string };

    setSaving(false);

    if (res?.error) {
      setError(res.error);
      return;
    }

    onSaved();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-2xl border border-white/[0.08] bg-slate-900 p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-100">
            {isEdit ? 'Редактировать ключ' : 'Новый API ключ'}
          </h2>
          <button onClick={onClose} className="p-1 text-slate-500 hover:text-slate-300">
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={save} autoComplete="off" className="space-y-4">
          <div>
            <label className="block text-sm text-slate-400 mb-1">Провайдер</label>
            <select value={provider} onChange={(e) => setProvider(e.target.value)} className="input">
              <option value="google">Google (Gemini)</option>
              <option value="grok">Grok (xAI)</option>
              <option value="openai">OpenAI (GPT Image)</option>
              <option value="yandex">Yandex (YandexART)</option>
              <option value="suno">Suno (Music)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-1">Название</label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="input"
              placeholder="Prod Key #1"
              required
            />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-1">
              {isEdit ? 'Новый ключ (оставьте пустым)' : 'Ключ'}
            </label>
            <input
              type="password"
              autoComplete="off"
              value={keyValue}
              onChange={(e) => setKeyValue(e.target.value)}
              className="input font-mono"
              placeholder={isEdit ? '(не менять)'
                : provider === 'grok'   ? 'xai-...'
                : provider === 'openai' ? 'sk-proj-...'
                : provider === 'yandex' ? 'AQVN...'
                : provider === 'suno'   ? 'su_...'
                : 'AIza...'}
              required={!isEdit}
            />
          </div>

          {/* Enable/Disable toggle */}
          <div>
            <label className="block text-sm text-slate-400 mb-1.5">Статус ключа</label>
            <button
              type="button"
              onClick={() => setEnabled(!enabled)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition w-full ${
                enabled
                  ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/25'
                  : 'bg-red-500/10 text-red-400 border-red-500/25'
              }`}
            >
              {enabled ? (
                <ToggleRight className="w-4 h-4" />
              ) : (
                <ToggleLeft className="w-4 h-4" />
              )}
              {enabled ? 'Активен' : 'Отключен'}
            </button>
          </div>

          {/* User access picker */}
          <div>
            <label className="block text-sm text-slate-400 mb-1.5">Доступ пользователей</label>
            <button
              type="button"
              onClick={() => {
                setAllUsersMode(!allUsersMode);
                if (!allUsersMode) setSelectedUsers([]);
              }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition mb-2 ${
                allUsersMode
                  ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                  : 'bg-slate-800 text-slate-500 border-white/[0.06] hover:border-white/[0.12]'
              }`}
            >
              Все пользователи
            </button>

            {!allUsersMode && (
              <div className="space-y-2">
                <input
                  type="text"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="input text-xs"
                  placeholder="Поиск по имени или ID..."
                />
                {loadingUsers ? (
                  <div className="text-xs text-slate-600 py-2">Загрузка пользователей...</div>
                ) : (
                  <div className="max-h-40 overflow-y-auto rounded-lg border border-white/[0.06] bg-slate-950/40">
                    {filteredUsers.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-slate-600">Пользователи не найдены</div>
                    ) : (
                      filteredUsers.map((user) => {
                        const isSelected = selectedUsers.includes(user.id);
                        return (
                          <button
                            key={user.id}
                            type="button"
                            onClick={() => toggleUser(user.id)}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-left text-xs transition border-b border-white/[0.03] last:border-0 ${
                              isSelected
                                ? 'bg-indigo-500/10 text-indigo-300'
                                : 'text-slate-400 hover:bg-white/[0.03]'
                            }`}
                          >
                            <div
                              className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition ${
                                isSelected
                                  ? 'bg-indigo-500 border-indigo-500'
                                  : 'border-slate-600'
                              }`}
                            >
                              {isSelected && <Check className="w-3 h-3 text-white" />}
                            </div>
                            <span className="font-medium">{user.username}</span>
                            <span className="text-slate-600 ml-auto">{user.id}</span>
                          </button>
                        );
                      })
                    )}
                  </div>
                )}
                {selectedUsers.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {selectedUsers.map((uid) => {
                      const u = userList.find((x) => x.id === uid);
                      return (
                        <span
                          key={uid}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-indigo-500/15 text-indigo-300 border border-indigo-500/25"
                        >
                          {u?.username || uid}
                          <button
                            type="button"
                            onClick={() => toggleUser(uid)}
                            className="hover:text-red-400 transition"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-lg bg-slate-800 text-slate-400 hover:bg-slate-700 text-sm transition"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm transition disabled:opacity-50"
            >
              {saving ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Statistics ──────────────────────────────────────────────────────

function StatsPanel() {
  const [stats, setStats] = useState<{
    users: Record<string, UserStats>;
    timeline: Record<string, { tokens: number; cost: number; count: number }>;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [rubRate, setRubRate] = useState<number>(() => {
    const saved = localStorage.getItem('wite_ai_rub_rate');
    return saved ? parseFloat(saved) : 95;
  });
  const updateRubRate = (val: string) => {
    const num = parseFloat(val);
    if (!isNaN(num) && num > 0) {
      setRubRate(num);
      localStorage.setItem('wite_ai_rub_rate', String(num));
    }
  };

  const fmtCost = (usd: number) => {
    return `$${usd.toFixed(4)} (${(usd * rubRate).toFixed(2)} ₽)`;
  };

  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const data = (await siteProxy('ai', '/api/admin/stats')) as typeof stats;
        setStats(data);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Не удалось загрузить статистику');
      }
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="py-4 text-sm text-slate-500">Загрузка...</div>;
  if (error) return <div className="py-4 text-sm text-red-400">Ошибка: {error}</div>;
  if (!stats) return <div className="py-4 text-sm text-slate-600">Нет данных</div>;

  const userEntries = Object.entries(stats.users || {});
  const timelineEntries = Object.entries(stats.timeline || {}).sort((a, b) =>
    b[0].localeCompare(a[0]),
  );

  return (
    <div className="pt-4 space-y-6">
      {/* RUB rate */}
      <div className="flex items-center gap-4 p-3 rounded-lg bg-slate-800/40 border border-white/[0.06]">
        <span className="text-amber-400 text-sm font-medium">₽</span>
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <span>Курс USD → RUB:</span>
          <input
            type="number"
            value={rubRate}
            onChange={e => updateRubRate(e.target.value)}
            className="w-20 bg-slate-800 border border-white/[0.08] rounded px-2 py-1 text-slate-200 text-xs"
            min={1}
            step={0.5}
          />
        </div>
        <span className="text-[11px] text-slate-600 ml-auto">Рубли видны при РУ языке в AI Lab</span>
      </div>
      {/* Per-user stats */}
      <div>
        <h3 className="text-sm font-medium text-slate-300 mb-3">По пользователям</h3>
        {userEntries.length === 0 ? (
          <p className="text-sm text-slate-600">Нет данных</p>
        ) : (
          <div className="rounded-lg border border-white/[0.06] overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-800/60 text-slate-400">
                  <th className="text-left px-4 py-2 font-medium">Пользователь</th>
                  <th className="text-right px-4 py-2 font-medium">Генерации</th>
                  <th className="text-right px-4 py-2 font-medium">Токены</th>
                  <th className="text-right px-4 py-2 font-medium">Стоимость</th>
                </tr>
              </thead>
              <tbody>
                {userEntries.map(([userId, s]) => (
                  <tr key={userId} className="border-t border-white/[0.04]">
                    <td className="px-4 py-2 text-slate-300">{userId}</td>
                    <td className="px-4 py-2 text-right text-slate-400">{s.count}</td>
                    <td className="px-4 py-2 text-right text-slate-400">
                      {s.totalTokens.toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-right text-emerald-400">
                      {fmtCost(s.totalCost)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Timeline */}
      <div>
        <h3 className="text-sm font-medium text-slate-300 mb-3">По дням</h3>
        {timelineEntries.length === 0 ? (
          <p className="text-sm text-slate-600">Нет данных</p>
        ) : (
          <div className="rounded-lg border border-white/[0.06] overflow-hidden max-h-64 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-800/90">
                <tr className="text-slate-400">
                  <th className="text-left px-4 py-2 font-medium">Дата</th>
                  <th className="text-right px-4 py-2 font-medium">Генерации</th>
                  <th className="text-right px-4 py-2 font-medium">Токены</th>
                  <th className="text-right px-4 py-2 font-medium">Стоимость</th>
                </tr>
              </thead>
              <tbody>
                {timelineEntries.map(([date, s]) => (
                  <tr key={date} className="border-t border-white/[0.04]">
                    <td className="px-4 py-2 text-slate-300">{date}</td>
                    <td className="px-4 py-2 text-right text-slate-400">{s.count}</td>
                    <td className="px-4 py-2 text-right text-slate-400">
                      {s.tokens.toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-right text-emerald-400">
                      {fmtCost(s.cost)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Global Presets ──────────────────────────────────────────────────

function PresetsPanel() {
  const [presets, setPresets] = useState<Preset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = (await siteProxy('ai', '/api/presets')) as Preset[];
      setPresets(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (!name.trim() || !content.trim()) return;
    setSaving(true);
    try {
      await siteProxy('ai', '/api/presets', {
        method: 'POST',
        body: { name: name.trim(), content: content.trim(), userId: 'admin' },
      });
      setName('');
      setContent('');
      setEditing(false);
      load();
    } catch { /* handled by reload */ }
    setSaving(false);
  };

  const remove = async (presetName: string) => {
    if (!confirm(`Удалить пресет "${presetName}"?`)) return;
    try {
      await siteProxy('ai', `/api/presets?name=${encodeURIComponent(presetName)}&userId=admin`, {
        method: 'DELETE',
        body: { userId: 'admin' },
      });
      load();
    } catch { /* handled by reload */ }
  };

  const edit = (p: Preset) => {
    setName(p.name);
    setContent(p.content);
    setEditing(true);
  };

  const move = async (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= presets.length) return;
    const reordered = [...presets];
    [reordered[idx], reordered[target]] = [reordered[target], reordered[idx]];
    setPresets(reordered);
    try {
      await siteProxy('ai', '/api/presets/reorder', {
        method: 'POST',
        body: { names: reordered.map((p) => p.name) },
      });
    } catch {
      load();
    }
  };

  if (loading) return <div className="py-4 text-sm text-slate-500">Загрузка...</div>;
  if (error) return <div className="py-4 text-sm text-red-400">Ошибка: {error}</div>;

  return (
    <div className="pt-4 space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* List */}
        <div className="space-y-2">
          <p className="text-xs text-slate-500 font-medium mb-2">Существующие пресеты</p>
          {presets.length === 0 ? (
            <p className="text-sm text-slate-600 py-4 text-center">Нет пресетов</p>
          ) : (
            presets.map((p, idx) => (
              <div
                key={p.name}
                className="flex items-center gap-2 p-3 rounded-lg bg-slate-900/60 border border-white/[0.06]"
              >
                <div className="flex flex-col gap-0.5">
                  <button
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0}
                    className="p-0.5 rounded text-slate-500 hover:text-indigo-400 transition disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Выше"
                  >
                    <ArrowUp className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => move(idx, 1)}
                    disabled={idx === presets.length - 1}
                    className="p-0.5 rounded text-slate-500 hover:text-indigo-400 transition disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Ниже"
                  >
                    <ArrowDown className="w-3 h-3" />
                  </button>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-200 truncate">{p.name}</p>
                  <p className="text-xs text-slate-500 truncate">{p.content}</p>
                </div>
                <button
                  onClick={() => edit(p)}
                  className="p-1.5 rounded text-slate-500 hover:text-indigo-400 transition"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => remove(p.name)}
                  className="p-1.5 rounded text-slate-500 hover:text-red-400 transition"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))
          )}
        </div>

        {/* Create / Edit */}
        <div className="p-4 rounded-lg bg-slate-900/60 border border-white/[0.06] space-y-3">
          <p className="text-xs text-slate-500 font-medium">
            {editing ? 'Редактировать пресет' : 'Новый пресет'}
          </p>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="input"
            placeholder="Имя пресета"
            disabled={editing}
          />
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="input min-h-[120px] resize-y"
            placeholder="Системные инструкции..."
          />
          <div className="flex gap-2">
            <button
              onClick={save}
              disabled={saving || !name.trim() || !content.trim()}
              className="px-3 py-1.5 rounded-lg text-xs bg-indigo-600 hover:bg-indigo-500 text-white transition disabled:opacity-50"
            >
              {saving ? 'Сохранение...' : editing ? 'Обновить' : 'Создать'}
            </button>
            {editing && (
              <button
                onClick={() => {
                  setEditing(false);
                  setName('');
                  setContent('');
                }}
                className="px-3 py-1.5 rounded-lg text-xs bg-slate-800 text-slate-400 hover:bg-slate-700 transition"
              >
                Отмена
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Gallery Access Key ─────────────────────────────────────────────

function GalleryKeyPanel() {
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const data = (await siteProxy('ai', '/api/key')) as { apiKey?: string };
        setApiKey(data?.apiKey || null);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Не удалось загрузить');
      }
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="py-4 text-sm text-slate-500">Загрузка...</div>;
  if (error) return <div className="py-4 text-sm text-red-400">Ошибка: {error}</div>;

  return (
    <div className="pt-4 space-y-3">
      <p className="text-xs text-slate-500">
        Ключ для доступа к внешней галерее через API
      </p>
      {apiKey ? (
        <div className="p-3 rounded-lg bg-slate-950/60 border border-emerald-500/20">
          <code className="text-sm text-emerald-400 select-all break-all">{apiKey}</code>
        </div>
      ) : (
        <p className="text-sm text-slate-600">Ключ не настроен</p>
      )}
      <p className="text-[11px] text-slate-600">
        Использование: <code className="text-slate-500">GET /api/external_gallery?key=YOUR_KEY</code>
      </p>
    </div>
  );
}

// ─── Gallery Visibility Panel ────────────────────────────────────────

interface AiLabUser {
  id: string;
  username: string;
  role: string;
  allowedModels?: string[];
}

function GalleryVisibilityPanel() {
  const [users, setUsers] = useState<AiLabUser[]>([]);
  const [hiddenUsers, setHiddenUsers] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [usersList, settings] = await Promise.all([
        siteProxy('ai', '/api/users') as Promise<AiLabUser[]>,
        siteProxy('ai', '/api/system-settings') as Promise<SystemSettings>,
      ]);
      setUsers(Array.isArray(usersList) ? usersList : []);
      setHiddenUsers(Array.isArray(settings?.externalGalleryHiddenUsers) ? settings.externalGalleryHiddenUsers : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleUser = (userId: string) => {
    setHiddenUsers(prev =>
      prev.includes(userId) ? prev.filter(u => u !== userId) : [...prev, userId]
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      const settings = (await siteProxy('ai', '/api/system-settings')) as SystemSettings;
      await siteProxy('ai', '/api/system-settings', {
        method: 'POST',
        body: { ...settings, externalGalleryHiddenUsers: hiddenUsers },
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка сохранения');
    }
    setSaving(false);
  };

  if (loading) return <div className="py-4 text-sm text-slate-500">Загрузка...</div>;
  if (error) return <div className="py-4 text-sm text-red-400">Ошибка: {error}</div>;

  return (
    <div className="pt-4 space-y-4">
      <p className="text-xs text-slate-500">
        Скрытые пользователи не отображаются во внешней галерее (API <code className="text-slate-600">/api/external_gallery</code>)
      </p>

      {users.length === 0 ? (
        <p className="text-sm text-slate-600">Нет пользователей</p>
      ) : (
        <div className="space-y-2">
          {users.map(user => {
            const isHidden = hiddenUsers.includes(user.id);
            return (
              <button
                key={user.id}
                onClick={() => toggleUser(user.id)}
                className={`w-full flex items-center justify-between p-3 rounded-lg border transition ${
                  isHidden
                    ? 'bg-red-500/5 border-red-500/20 hover:border-red-500/30'
                    : 'bg-slate-900/60 border-white/[0.06] hover:border-white/[0.1]'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                      user.role === 'admin'
                        ? 'bg-indigo-500/20 text-indigo-400'
                        : 'bg-slate-700 text-slate-300'
                    }`}
                  >
                    {user.username.charAt(0).toUpperCase()}
                  </div>
                  <span className="text-sm text-slate-200">{user.username}</span>
                  <span className="text-[10px] text-slate-600">({user.id})</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs ${isHidden ? 'text-red-400' : 'text-emerald-400'}`}>
                    {isHidden ? 'Скрыт' : 'Виден'}
                  </span>
                  {isHidden ? (
                    <EyeOff className="w-4 h-4 text-red-400" />
                  ) : (
                    <Eye className="w-4 h-4 text-emerald-400" />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      <div className="flex gap-3 pt-2">
        <button
          onClick={save}
          disabled={saving}
          className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm transition disabled:opacity-50"
        >
          {saving ? 'Сохранение...' : saved ? '✓ Сохранено' : 'Сохранить'}
        </button>
        <button
          onClick={load}
          className="px-4 py-2 rounded-lg bg-slate-800 text-slate-400 hover:bg-slate-700 text-sm transition"
        >
          Сбросить
        </button>
      </div>
    </div>
  );
}

// ─── S3 Storage Panel ────────────────────────────────────────────────

function S3Panel() {
  const [s3Status, setS3Status] = useState<S3Status | null>(null);
  const [migration, setMigration] = useState<AiMigrationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [migrating, setMigrating] = useState(false);
  const [progress, setProgress] = useState<AiMigrationProgress | null>(null);
  const [toggling, setToggling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [s3, mig] = await Promise.all([
        siteProxy('ai', '/api/admin/s3-status') as Promise<S3Status>,
        siteProxy('ai', '/api/admin/s3-migration/status') as Promise<AiMigrationStatus>,
      ]);
      setS3Status(s3);
      setMigration(mig);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const startMigration = async () => {
    if (!confirm('Начать миграцию изображений в S3?')) return;
    setMigrating(true);
    await siteProxy('ai', '/api/admin/s3-migration/start', { method: 'POST' });

    const poll = setInterval(async () => {
      try {
        const data = (await siteProxy('ai', '/api/admin/s3-migration/progress')) as {
          progress: AiMigrationProgress | null;
        };
        setProgress(data?.progress ?? null);
        if (data?.progress?.phase === 'done') {
          clearInterval(poll);
          setMigrating(false);
          load();
        }
      } catch {
        clearInterval(poll);
        setMigrating(false);
      }
    }, 2000);
  };

  if (loading) return <div className="py-4 text-sm text-slate-500">Загрузка...</div>;
  if (error) return <div className="py-4 text-sm text-red-400">Ошибка: {error}</div>;

  return (
    <div className="pt-4 space-y-4">
      {/* Status */}
      {s3Status && (
        <div className="p-4 rounded-lg bg-slate-900/60 border border-white/[0.06] space-y-2">
          <div className="flex items-center gap-2">
            <Database className="w-4 h-4 text-slate-400" />
            <span className="text-sm font-medium text-slate-200">Статус S3</span>
            <span
              className={`px-2 py-0.5 text-[10px] rounded-full font-medium ${
                s3Status.s3Enabled
                  ? 'bg-emerald-500/10 text-emerald-400'
                  : 'bg-slate-700 text-slate-500'
              }`}
            >
              {s3Status.s3Enabled ? 'Включен' : 'Отключен'}
            </span>
            <button
              onClick={async () => {
                const next = !s3Status.s3Enabled;
                if (!confirm(next
                  ? 'Включить S3? Изображения будут загружаться в облако. Требуется перезапуск сервера.'
                  : 'Выключить S3? Изображения будут сохраняться локально. Требуется перезапуск сервера.',
                )) return;
                setToggling(true);
                try {
                  await siteProxy('ai', '/api/admin/s3-toggle', {
                    method: 'POST',
                    body: { enabled: next },
                  });
                  setS3Status((prev) => prev ? { ...prev, s3Enabled: next } : prev);
                } catch { /* ignore */ }
                setToggling(false);
              }}
              disabled={toggling}
              className={`ml-auto px-3 py-1 rounded-lg text-xs font-medium transition ${
                s3Status.s3Enabled
                  ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20'
                  : 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20'
              } disabled:opacity-50`}
            >
              {toggling ? '...' : s3Status.s3Enabled ? 'Выключить' : 'Включить'}
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <span className="text-slate-600">Endpoint:</span>{' '}
              <span className="text-slate-400">{s3Status.endpoint || '—'}</span>
            </div>
            <div>
              <span className="text-slate-600">Bucket:</span>{' '}
              <span className="text-slate-400">{s3Status.bucket || '—'}</span>
            </div>
            <div>
              <span className="text-slate-600">Prefix:</span>{' '}
              <span className="text-slate-400">{s3Status.keyPrefix || '—'}</span>
            </div>
            <div>
              <span className="text-slate-600">CDN:</span>{' '}
              <span className="text-slate-400">{s3Status.cdnUrl || '—'}</span>
            </div>
            <div>
              <span className="text-slate-600">Галерея:</span>{' '}
              <span className="text-slate-400">{s3Status.galleryBucket || '—'}</span>
            </div>
            <div>
              <span className="text-slate-600">Галерея:</span>{' '}
              <span
                className={`${
                  s3Status.galleryEnabled ? 'text-emerald-400' : 'text-slate-500'
                }`}
              >
                {s3Status.galleryEnabled ? 'Подключена' : 'Отключена'}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Migration status */}
      {migration && (
        <div className="p-4 rounded-lg bg-slate-900/60 border border-white/[0.06]">
          <h3 className="text-sm font-medium text-slate-200 mb-3">Миграция изображений</h3>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="text-center p-2 rounded-lg bg-slate-950/60">
              <p className="text-xs text-slate-500 mb-1">В S3</p>
              <p className="text-lg font-semibold text-slate-200">{migration.existing.images}</p>
            </div>
            <div className="text-center p-2 rounded-lg bg-slate-950/60">
              <p className="text-xs text-slate-500 mb-1">Ожидает</p>
              <p className="text-lg font-semibold text-slate-200">{migration.pending.images}</p>
              {migration.pending.images > 0 && (
                <p className="text-[10px] text-amber-400">Нужно загрузить</p>
              )}
            </div>
          </div>

          {migration.pending.total > 0 && (
            <button
              onClick={startMigration}
              disabled={migrating}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-indigo-600 hover:bg-indigo-500 text-white transition disabled:opacity-50"
            >
              <Upload className="w-4 h-4" />
              {migrating ? 'Миграция...' : `Загрузить ${migration.pending.total} файлов`}
            </button>
          )}

          {progress && migrating && (
            <div className="mt-3 p-3 rounded-lg bg-slate-950/60 text-xs">
              <p className="text-slate-400">
                Фаза: {progress.phase} | {progress.uploaded}/{progress.total}
              </p>
              {progress.errors.length > 0 && (
                <p className="text-red-400 mt-1">Ошибки: {progress.errors.length}</p>
              )}
              <div className="mt-2 bg-slate-800 rounded-full h-1.5">
                <div
                  className="bg-indigo-500 rounded-full h-1.5 transition-all"
                  style={{
                    width: `${progress.total > 0 ? (progress.uploaded / progress.total) * 100 : 0}%`,
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Gallery Migration Panel ─────────────────────────────────────────

function GalleryMigrationPanel() {
  const [status, setStatus] = useState<GalleryMigrationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [migrating, setMigrating] = useState(false);
  const [progress, setProgress] = useState<AiMigrationProgress | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = (await siteProxy('ai', '/api/admin/gallery-migration/status')) as GalleryMigrationStatus;
      setStatus(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const startMigration = async () => {
    if (!confirm('Начать миграцию галереи в приватный бакет?')) return;
    setMigrating(true);
    await siteProxy('ai', '/api/admin/gallery-migration/start', { method: 'POST' });

    const poll = setInterval(async () => {
      try {
        const data = (await siteProxy('ai', '/api/admin/gallery-migration/progress')) as {
          progress: AiMigrationProgress | null;
        };
        setProgress(data?.progress ?? null);
        if (data?.progress?.phase === 'done') {
          clearInterval(poll);
          setMigrating(false);
          load();
        }
      } catch {
        clearInterval(poll);
        setMigrating(false);
      }
    }, 2000);
  };

  if (loading) return <div className="py-4 text-sm text-slate-500">Загрузка...</div>;
  if (error) return <div className="py-4 text-sm text-red-400">Ошибка: {error}</div>;

  return (
    <div className="pt-4 space-y-4">
      <p className="text-xs text-slate-500">
        Перенос изображений из witelab-storage → ai-witegallery (приватный бакет, доступ только по presigned URL)
      </p>

      {status && (
        <div className="p-4 rounded-lg bg-slate-900/60 border border-white/[0.06]">
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="text-center p-2 rounded-lg bg-slate-950/60">
              <p className="text-xs text-slate-500 mb-1">В бакете</p>
              <p className="text-lg font-semibold text-slate-200">{status.existing}</p>
            </div>
            <div className="text-center p-2 rounded-lg bg-slate-950/60">
              <p className="text-xs text-slate-500 mb-1">Ожидает</p>
              <p className="text-lg font-semibold text-slate-200">{status.pending}</p>
              {status.pending > 0 && (
                <p className="text-[10px] text-amber-400">Нужно загрузить</p>
              )}
            </div>
          </div>

          {status.pending > 0 && (
            <button
              onClick={startMigration}
              disabled={migrating}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-purple-600 hover:bg-purple-500 text-white transition disabled:opacity-50"
            >
              <Upload className="w-4 h-4" />
              {migrating ? 'Миграция...' : `Загрузить ${status.pending} файлов`}
            </button>
          )}

          {progress && migrating && (
            <div className="mt-3 p-3 rounded-lg bg-slate-950/60 text-xs">
              <p className="text-slate-400">
                Фаза: {progress.phase} | {progress.uploaded}/{progress.total}
              </p>
              {progress.errors.length > 0 && (
                <p className="text-red-400 mt-1">Ошибки: {progress.errors.length}</p>
              )}
              <div className="mt-2 bg-slate-800 rounded-full h-1.5">
                <div
                  className="bg-purple-500 rounded-full h-1.5 transition-all"
                  style={{
                    width: `${progress.total > 0 ? (progress.uploaded / progress.total) * 100 : 0}%`,
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

